import type { ConversationMessage } from '@chatbot/shared-types';
import { NotaDeVoz } from './NotaDeVoz.tsx';

/**
 * El archivo que mandó la persona, dentro de su burbuja.
 *
 * Sin `url` no se dibuja un reproductor: se dice que hubo un archivo y que no se
 * puede abrir. Un `<audio>` con src vacío se ve como un control roto, y el
 * operador no sabría si el problema es el archivo o el panel.
 */
export function MedioAdjunto({
  medio,
  id,
}: {
  medio: NonNullable<ConversationMessage['medio']>;
  /**
   * Identificador estable del mensaje.
   *
   * La onda de respaldo se deriva de acá y no de la url: la url viene firmada y
   * cambia en cada consulta, así que la onda cambiaría al recargar — y una onda
   * que se mueve sola se lee como un error del panel.
   */
  id: string;
}) {
  const etiqueta = {
    audio: 'Nota de voz',
    image: 'Imagen',
    video: 'Video',
    document: 'Documento',
    sticker: 'Sticker',
  }[medio.tipo];

  if (!medio.url) {
    return (
      <div className="mt-1 rounded-lg bg-black/20 px-3 py-2 text-xs">
        {etiqueta} — ya no está disponible
      </div>
    );
  }

  return (
    <div className="mt-1">
      {medio.tipo === 'audio' ? (
        // `controls` y nada más: sin autoplay, que en una bandeja con varias
        // conversaciones abiertas sonaría solo.
        // Reproductor propio con onda. El `<audio controls>` nativo colapsaba
        // sus mandos en una burbuja angosta —medido en producción— y además no
        // dice nada del contenido: en una bandeja con veinte conversaciones,
        // ver de un vistazo si son tres palabras o dos minutos cambia cómo se
        // prioriza.
        <NotaDeVoz url={medio.url} id={id} />
      ) : medio.tipo === 'image' || medio.tipo === 'sticker' ? (
        <a href={medio.url} target="_blank" rel="noopener noreferrer">
          <img
            src={medio.url}
            alt={medio.caption ?? etiqueta}
            className="max-h-64 rounded-lg"
            loading="lazy"
          />
        </a>
      ) : medio.tipo === 'video' ? (
        <video controls preload="none" src={medio.url} className="max-h-64 rounded-lg">
          <track kind="captions" />
        </video>
      ) : (
        <a
          href={medio.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded-lg bg-black/20 px-3 py-2 text-xs underline"
        >
          📄 {etiqueta}
        </a>
      )}
      {/* El pie de foto es texto de la persona: se muestra como tal, no como
          metadato del archivo. */}
      {medio.caption ? <div className="mt-1 text-sm">{medio.caption}</div> : null}
    </div>
  );
}
